import { useEffect, useRef, useState } from 'react';
import { db } from '../lib/firebase';
import {
  collection, query, orderBy, limit,
  onSnapshot, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { useVoiceRecorder, uploadVoice } from '../hooks/useVoiceRecorder';

function genWave() {
  return Array.from({ length: 20 }, () => Math.floor(Math.random() * 16) + 4);
}

function fmtTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtDur(sec) {
  if (!sec) return '0:00';
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

function VoiceMessage({ msg, isMe, msgId }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [wave] = useState(genWave);

  function toggle() {
    if (!msg.audioUrl) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(msg.audioUrl);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else          { audioRef.current.play();  setPlaying(true);  }
  }

  return (
    <div className="voice-msg" style={isMe ? { background: 'var(--amber-glow)', borderColor: 'rgba(245,166,35,0.2)' } : {}}>
      <button className="voice-play" onClick={toggle}>
        <i className={`ti ${playing ? 'ti-player-pause' : 'ti-player-play'}`} />
      </button>
      <div className="waveform">
        {wave.map((h, i) => (
          <span key={i} style={{ height: h, opacity: playing ? 1 : 0.6 }} />
        ))}
      </div>
      <span className="voice-dur">{fmtDur(msg.duration)}</span>
    </div>
  );
}

function Message({ msg, currentUid }) {
  const isMe = msg.senderId === currentUid;
  return (
    <div className={`msg-row${isMe ? ' me' : ''}`}>
      <div className="avatar sm" style={{ background: msg.senderAvatarBg, color: msg.senderAvatarText }}>
        {msg.senderInitials}
      </div>
      <div className="msg-content">
        {!isMe && <div className="msg-sender">{msg.senderName}</div>}
        {msg.type === 'voice'
          ? <VoiceMessage msg={msg} isMe={isMe} msgId={msg.id} />
          : <div className="msg-bubble">{msg.text}</div>
        }
        <div className="msg-time">{fmtTime(msg.timestamp)}</div>
      </div>
    </div>
  );
}

export default function ChatScreen({ user, userData, onNavigate }) {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const areaRef = useRef(null);
  const { recording, startRecording, stopRecording } = useVoiceRecorder();

  useEffect(() => {
    const q = query(
      collection(db, 'groups', 'main', 'messages'),
      orderBy('timestamp', 'asc'), limit(60)
    );
    return onSnapshot(q, snap => {
      const msgs = [];
      snap.forEach(d => msgs.push({ id: d.id, ...d.data() }));
      setMessages(msgs);
      setTimeout(() => {
        if (areaRef.current) areaRef.current.scrollTop = areaRef.current.scrollHeight;
      }, 50);
    });
  }, []);

  async function sendText() {
    if (!text.trim() || sending) return;
    setSending(true);
    await addDoc(collection(db, 'groups', 'main', 'messages'), {
      text:           text.trim(),
      senderId:       user.uid,
      senderName:     userData.displayName,
      senderInitials: userData.initials,
      senderAvatarBg: userData.avatarBg,
      senderAvatarText: userData.avatarText,
      type:           'text',
      timestamp:      serverTimestamp(),
    });
    setText('');
    setSending(false);
  }

  function handleVoiceStop() {
    stopRecording(async (blob, duration) => {
      try {
        const audioUrl = await uploadVoice(blob, user.uid);
        await addDoc(collection(db, 'groups', 'main', 'messages'), {
          text:           '',
          senderId:       user.uid,
          senderName:     userData.displayName,
          senderInitials: userData.initials,
          senderAvatarBg: userData.avatarBg,
          senderAvatarText: userData.avatarText,
          type:           'voice',
          audioUrl,
          duration,
          timestamp:      serverTimestamp(),
        });
      } catch (e) {
        console.error('voice send failed', e);
      }
    });
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <div className="screen-title">messages</div>
        <div className="header-action" onClick={() => onNavigate('voice')}>
          <i className="ti ti-phone" />
        </div>
      </div>

      <div className="scroll-area" ref={areaRef} style={{ paddingTop: 8 }}>
        {messages.map(msg => (
          <Message key={msg.id} msg={msg} currentUid={user.uid} />
        ))}
      </div>

      <div className="chat-input-bar">
        <button
          className="voice-hold-btn"
          onMouseDown={startRecording}
          onMouseUp={handleVoiceStop}
          onTouchStart={startRecording}
          onTouchEnd={handleVoiceStop}
        >
          <i className="ti ti-microphone" />
        </button>
        <input
          className="chat-input"
          placeholder="say something..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendText()}
        />
        <button className="send-btn" onClick={sendText}>
          <i className="ti ti-send" />
        </button>
      </div>

      {recording && (
        <div className="rec-overlay">
          <div className="rec-circle"><i className="ti ti-microphone" /></div>
          <div className="rec-label">recording... release to send</div>
        </div>
      )}
    </div>
  );
}
